import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Building2,
  DollarSign,
  Users,
  MapPin,
  Plus,
  Pencil,
  Video,
  Star,
  FileText,
  Headphones,
  Trash2,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { EditActionBar } from '@/components/EditActionBar'
import { toDisplayDate } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { CurrencySelector } from '@/components/ui/currency-selector'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ActivityTimeline } from '@/components/ActivityTimeline'
import { HistoryTimeline } from '@/components/HistoryTimeline'
import { RelatedEmails } from '@/components/RelatedEmails'
import { ContentDetailModal } from '@/components/ContentDetailModal'
import { NoteDetailModal } from '@/components/NoteDetailModal'
import { EditNoteModal } from '@/components/EditNoteModal'
import { MergedNotesList } from '@/components/notes/MergedNotesList'
import { NoteSessionPanel } from '@/components/notes/NoteSessionPanel'
import { useDeleteNote, useDeleteNoteSession } from '@/hooks/use-api'
import type { Note } from '@/types'
import type { NoteSession } from '@/types/noteSession'
import { ownerPickerOptions } from '@/lib/salesReps'
import { CreateTaskModal } from '@/components/CreateTaskModal'
import { CreateContactModal } from '@/components/CreateContactModal'
import { CreateOpportunityModal } from '@/components/CreateOpportunityModal'
import { CreateRfpModal } from '@/components/CreateRfpModal'
import { CreateTicketModal } from '@/components/CreateTicketModal'
import { AttachmentsTab } from '@/components/AttachmentsTab'
import { ContractsTab } from '@/components/ContractsTab'
import { ScheduleMeetDialog } from '@/components/ScheduleMeetDialog'
import { ClickToCallButton } from '@/components/ClickToCallButton'
import { AddressAutocomplete, type AddressDetails } from '@/components/ui/address-autocomplete'
import {
  useAccount,
  useUpdateAccount,
  useUsers,
  useAccountHealth,
  useAccountEngagement,
  useEntityContent,
  useRfps,
  useTickets,
  useAccounts,
  useNotes,
  useDeleteAccount,
  useLeads,
  useNoteSessionsByAccount,
} from '@/hooks/use-api'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useUser } from '@/contexts/UserContext'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { ticketStatusVariants, ticketPriorityVariants } from '@/lib/ticket-variants'
import { usePicklistOptions } from '@/hooks/use-picklists'
import { useToast } from '@/hooks/use-toast'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useRecordBreadcrumb } from '@/contexts/BreadcrumbContext'
import { useClickToEdit } from '@/hooks/use-click-to-edit'
import { useState } from 'react'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { EmailOnlyNotice } from '@/components/accounts/EmailOnlyNotice'
import type { Account } from '@/types'
import { AccountHealthCard } from '@/components/AccountHealthScore'
import { NextBestAction } from '@/components/NextBestAction'
import { GRACKLE_COLORS, CATEGORICAL } from '@/lib/theme'
import { explainError } from '@/lib/explain-error'

/**
 * One tab in the Related Information strip.
 *
 * Underline rather than the shadcn default, which is a filled tray of pills and reads as a
 * button group rather than tabs. Every colour is a theme token, never a hex literal, because
 * the brand guardrail runs at a zero baseline.
 *
 * Sized to its content instead of `flex-1`: equal widths made "Refs" take as much room as
 * "Children", which both wastes space in a narrow column and is not how tabs look.
 *
 * `cn()` is tailwind-merge, so `rounded-none` and `bg-transparent` genuinely replace the
 * primitive's `rounded-sm` and active `bg-background` rather than racing them in the stylesheet.
 */
const TAB_CLASS =
  // Outlined and filled, so each tab is a discrete object rather than a word in a row.
  // The border does most of that work: `muted` against the white card is only 1.15:1, too
  // faint to delimit anything on its own, whereas a 1px line reads as an edge immediately.
  'rounded-b-none rounded-t-md border border-b-2 border-border border-b-transparent ' +
  'bg-muted px-2.5 py-1.5 text-xs font-medium text-muted-foreground shadow-none ' +
  'transition-colors hover:bg-card hover:text-foreground ' +
  // The active tab takes the card's own colour, so it reads as the open one rather than
  // merely the highlighted one. Card is `bg-card` (pure white), which is NOT `bg-background`
  // (a tinted off-white) - the primitive's default active state uses the latter and would
  // have left a visible seam against the panel.
  'data-[state=active]:border-border data-[state=active]:border-b-primary ' +
  'data-[state=active]:bg-card data-[state=active]:text-foreground ' +
  'data-[state=active]:shadow-none'

function formatCustomField(value?: string | null): string {
  if (!value) return ''
  return value.replace(/[?_]+/g, ' ').replace(/\s+/g, ' ').trim()
}

export default function AccountDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()

  const { data, isLoading, error } = useAccount(id!)
  useDocumentTitle(data?.name ? `${data.name} | Account` : undefined)
  useRecordBreadcrumb(data?.name)
  const updateMutation = useUpdateAccount()
  const { data: usersData } = useUsers({ limit: 10000 })
  const { data: accountsData } = useAccounts({ limit: 10000 })
  const { data: accountHealth, isLoading: accountHealthLoading } = useAccountHealth(id!)
  const { data: accountEngagement } = useAccountEngagement(id!)
  const { data: contentData } = useEntityContent(id!)
  const { data: accountNotesData } = useNotes({ parent_id: id! })
  const accountNotes = accountNotesData?.data ?? []
  const { data: noteSessions } = useNoteSessionsByAccount(id!)
  const { currentUser, isAdmin, impersonatedUser, canManageTickets } = useUser()
  const deleteNoteMutation = useDeleteNote(id)
  const deleteSessionMutation = useDeleteNoteSession()
  // Only Admin/Super Admin can delete (for cleanup); blocked while impersonating a non-admin.
  const canDelete =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  const deleteAccountMutation = useDeleteAccount()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const ACCOUNT_TYPES = usePicklistOptions('account', 'type')
  const { data: rfpsData } = useRfps({ account_id: id, limit: 10000 } as any)
  const { data: ticketsData } = useTickets({ account_id: id, limit: 10000 })
  const { data: matchedLeadsData } = useLeads({ matched_account_id: id, limit: 10000 } as any)
  const rfps = rfpsData?.data || []
  const tickets = ticketsData?.data || []
  const matchedLeads = matchedLeadsData?.data || []

  // Content/Note modal state
  const [selectedContentId, setSelectedContentId] = useState<string | null>(null)
  const [contentDetailModalOpen, setContentDetailModalOpen] = useState(false)

  // Note session / legacy note state (editable notes, mirrors OpportunityDetail)
  const [notePanelOpen, setNotePanelOpen] = useState(false)
  const [existingSessionId, setExistingSessionId] = useState<string | undefined>(undefined)
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)
  const [noteDetailModalOpen, setNoteDetailModalOpen] = useState(false)
  const [editingNote, setEditingNote] = useState<Note | null>(null)
  const [editNoteModalOpen, setEditNoteModalOpen] = useState(false)
  const [editingSession, setEditingSession] = useState<NoteSession | null>(null)

  // Task modal state
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false)

  // Contact modal state
  const [createContactModalOpen, setCreateContactModalOpen] = useState(false)

  // Opportunity modal state
  const [createOpportunityModalOpen, setCreateOpportunityModalOpen] = useState(false)
  const [createRfpModalOpen, setCreateRfpModalOpen] = useState(false)
  const [createTicketModalOpen, setCreateTicketModalOpen] = useState(false)

  // Meet dialog state
  const [showMeetDialog, setShowMeetDialog] = useState(false)

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false)
  const [notifyOwner, setNotifyOwner] = useState(true)
  const [editForm, setEditForm] = useState<Partial<Account>>({})
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  // Click-to-edit hook — must be called before any conditional returns to
  // keep hook order stable. The lambda closes over `handleStartEdit` which
  // is defined later in the component; JS lexical scope means the lambda
  // reads the live binding when the user actually clicks (after render).
  const { bindReadOnly } = useClickToEdit(isEditMode, () => handleStartEdit())

  const users = usersData?.data || []
  const allAccounts = accountsData?.data || []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96" role="status" aria-live="polite">
        <div className="text-center">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="progressbar"
            aria-label="Loading account details"
          ></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading account details...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-96" role="alert">
        <p className="text-lg font-semibold text-destructive">Error loading account</p>
        <p className="text-sm text-muted-foreground mt-2">
          {explainError(error, 'Account not found')}
        </p>
        <Button
          onClick={() => navigate('/accounts')}
          className="mt-4"
          aria-label="Return to accounts list"
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to Accounts
        </Button>
      </div>
    )
  }

  const account = data

  // Extract notes (SNOTE content) from entity content
  const allContent = contentData?.data || []
  const notes = allContent.filter((item: any) => item.file_type === 'SNOTE')

  const formatCurrency = (amount?: number, currency = 'CAD') => {
    if (!amount) return 'N/A'
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
    } catch {
      return `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ${currency}`
    }
  }

  const formatDate = (date?: string) => {
    const d = toDisplayDate(date)
    if (!d) return 'N/A'
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatNumber = (num?: number) => {
    if (!num) return 'N/A'
    return new Intl.NumberFormat('en-US').format(num)
  }

  // Helper to safely format addresses from separate fields
  const formatAddress = () => {
    if (!account) return null
    const parts = [
      account?.billing_street,
      account?.billing_city,
      account?.billing_state,
      account?.billing_postal_code,
      account?.billing_country,
    ].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : null
  }

  // Parse JSON arrays from backend - only after we have data
  const recentContacts =
    ((account as unknown as Record<string, unknown>)?.recent_contacts as unknown[]) || []

  // "Request Trial Access" → open the Internal Support "Trial Request" form pre-filled
  // with this account's details (company, related account, sales rep = current user, and
  // the primary/first contact). The rep only picks Product + Trial Term there.
  const handleRequestTrial = () => {
    type AccountContact = {
      id?: string
      first_name?: string
      last_name?: string
      email?: string
      primary_contact__c?: boolean
    }
    const params = new URLSearchParams({ type: 'Trial Request' })
    if (account?.id) params.set('account_id', account.id)
    const companyName = account?.name || account?.account_name || ''
    if (companyName) params.set('company_name', companyName)
    if (currentUser?.id) params.set('sales_rep', currentUser.id)
    const contacts = recentContacts as AccountContact[]
    const primary = contacts.find((c) => c?.primary_contact__c) || contacts[0]
    if (primary) {
      const name = [primary.first_name, primary.last_name].filter(Boolean).join(' ').trim()
      const display = primary.email ? `${name} <${primary.email}>`.trim() : name
      if (display) params.set('contact', display)
      if (primary.id) params.set('contact_id', primary.id)
    }
    navigate(`/support-request?${params.toString()}`)
  }
  const recentOpportunities =
    ((account as unknown as Record<string, unknown>)?.recent_opportunities as unknown[]) || []

  // Edit mode handlers
  const handleDeleteConfirm = async () => {
    if (!account) return
    try {
      await deleteAccountMutation.mutateAsync(account.id)
      toast({
        title: 'Account deleted',
        description: `"${account.name || account.account_name}" has been deleted.`,
      })
      setDeleteDialogOpen(false)
      navigate('/accounts')
    } catch (error: any) {
      toast({
        title: 'Delete failed',
        description: explainError(error, 'Could not delete the account.'),
        variant: 'destructive',
      })
    }
  }

  const handleStartEdit = () => {
    setEditForm({
      name: account?.name || '',
      type: account?.type || '',
      industry: account?.industry || '',
      phone: account?.phone || '',
      website: account?.website || '',
      billing_street: account?.billing_street || '',
      billing_city: account?.billing_city || '',
      billing_state: account?.billing_state || '',
      billing_postal_code: account?.billing_postal_code || '',
      billing_country: account?.billing_country || '',
      description: account?.description || '',
      owner_id: account?.owner_id || '',
      currency_iso_code: account?.currency_iso_code || 'CAD',
      parent_account_id: (account as any)?.parent_account_id || '',
      number_of_employees: account?.number_of_employees ?? undefined,
      number_of_locations__c: account?.number_of_locations__c ?? undefined,
      type_of_shop__c: account?.type_of_shop__c || '',
      status__c: account?.status__c || '',
      marketing__c: account?.marketing__c || '',
      gdo_code__c: account?.gdo_code__c || '',
      digital_accessibility_proficiency__c: account?.digital_accessibility_proficiency__c || '',
      digital_policy__c: account?.digital_policy__c || '',
      current_digital_accessibility_vendor__c:
        account?.current_digital_accessibility_vendor__c || '',
      campaign__c: account?.campaign__c || '',
      billing_contact__c: account?.billing_contact__c || '',
    })
    setIsEditMode(true)
    setFormErrors({})
  }

  const handleCancelEdit = () => {
    setIsEditMode(false)
    setEditForm({})
    setFormErrors({})
  }

  const handleSaveEdit = async () => {
    // Validate required fields
    const errors: Record<string, string> = {}
    if (!editForm.name?.toString().trim()) errors.name = 'Account name is required'
    if (!editForm.billing_street?.toString().trim()) errors.billing_street = 'Street is required'
    if (!editForm.billing_city?.toString().trim()) errors.billing_city = 'City is required'

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      })
      return
    }

    try {
      await updateMutation.mutateAsync({
        id: account!.id,
        data: { ...editForm, notify_owner: notifyOwner },
      })
      toast({ title: 'Success', description: 'Account updated successfully.' })
      setIsEditMode(false)
      setFormErrors({})
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update account')
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' })
    }
  }

  const handleFieldChange = (field: string, value: string | number | boolean) => {
    setEditForm((prev) => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  // Render field helper - shows input in edit mode, display value otherwise
  const renderField = (
    label: string,
    field: string,
    value: string | number | undefined | null,
    options?: {
      type?: 'text' | 'select' | 'textarea' | 'number' | 'date'
      selectOptions?: string[]
      required?: boolean
      link?: { href: string; external?: boolean }
    }
  ) => {
    const { type = 'text', selectOptions, required, link } = options || {}
    const fieldId = `field-${field}`
    const errorId = `${fieldId}-error`

    if (isEditMode) {
      return (
        <div className="py-2 border-b border-border">
          <dt className="text-sm font-medium text-muted-foreground">
            {label}
            {required && ' *'}
          </dt>
          <dd className="mt-1">
            {type === 'textarea' ? (
              <textarea
                id={fieldId}
                value={(editForm[field as keyof typeof editForm] as string) || ''}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-required={required}
                aria-invalid={!!formErrors[field]}
                aria-describedby={formErrors[field] ? errorId : undefined}
              />
            ) : type === 'select' ? (
              <select
                id={fieldId}
                value={(editForm[field as keyof typeof editForm] as string) || ''}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-required={required}
                aria-invalid={!!formErrors[field]}
                aria-describedby={formErrors[field] ? errorId : undefined}
              >
                <option value="">Select...</option>
                {selectOptions?.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                id={fieldId}
                type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
                value={(editForm[field as keyof typeof editForm] as string | number) ?? ''}
                onChange={(e) =>
                  handleFieldChange(
                    field,
                    type === 'number' ? parseInt(e.target.value) || 0 : e.target.value
                  )
                }
                aria-required={required}
                aria-invalid={!!formErrors[field]}
                aria-describedby={formErrors[field] ? errorId : undefined}
              />
            )}
            {formErrors[field] && (
              <p id={errorId} className="text-sm text-destructive mt-1" role="alert">
                {formErrors[field]}
              </p>
            )}
          </dd>
        </div>
      )
    }

    // Display mode — clicking the value drops the page into edit mode
    // and focuses this field's input.
    const displayValue = value ?? 'N/A'
    const readProps = bindReadOnly(fieldId, label)
    return (
      <div className="py-2 border-b border-border">
        <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
        <dd
          {...readProps}
          className={`mt-1 text-sm flex items-center justify-between gap-2 ${readProps.className}`}
        >
          <span className="min-w-0 break-words whitespace-pre-wrap">
            {link && value ? (
              <a
                href={link.href}
                target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noopener noreferrer' : undefined}
                className="text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {displayValue}
              </a>
            ) : (
              displayValue
            )}
          </span>
          <Pencil className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden="true" />
        </dd>
      </div>
    )
  }

  // Special render for owner field with user dropdown
  const renderOwnerField = () => {
    if (isEditMode) {
      return (
        <div className="py-2 border-b border-border">
          <dt className="text-sm font-medium text-muted-foreground">Owner</dt>
          <dd className="mt-1">
            <select
              id="field-owner_id"
              value={(editForm.owner_id as string) || ''}
              onChange={(e) => handleFieldChange('owner_id', e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Select owner...</option>
              {ownerPickerOptions(users, account?.owner_id).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2 mt-2">
              <Checkbox
                id="field-notify_owner"
                checked={notifyOwner}
                onCheckedChange={(checked) => setNotifyOwner(checked === true)}
              />
              <Label htmlFor="field-notify_owner" className="text-sm font-normal cursor-pointer">
                Notify new owner by email (only if owner changes)
              </Label>
            </div>
          </dd>
        </div>
      )
    }
    return (
      <div className="py-2 border-b border-border">
        <dt className="text-sm font-medium text-muted-foreground">Owner</dt>
        <dd className="mt-1 text-sm">{account?.owner_name || 'N/A'}</dd>
      </div>
    )
  }

  return (
    <div className="py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/accounts')}
            aria-label="Back to accounts list"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </Button>
          <CategoryBadge type="account" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">
                {account?.name || account?.account_name}
              </h1>
              {recentContacts.some((c: any) => c.is_reference) && (
                <Badge
                  variant="default"
                  style={{
                    backgroundColor: GRACKLE_COLORS.pollen,
                    color: GRACKLE_COLORS.prussianBlue,
                  }}
                >
                  <Star className="h-3 w-3 mr-1" aria-hidden="true" />
                  Has Reference
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
              <Building2 className="h-4 w-4" aria-hidden="true" />
              <span>{account?.type || 'Account'}</span>
              {account?.industry && (
                <>
                  <span>•</span>
                  <span>{account?.industry}</span>
                </>
              )}
            </div>
            <EmailOnlyNotice engagement={accountEngagement} />
          </div>
        </div>
        {/* Save/Cancel live in the sticky EditActionBar while editing. */}
        {!isEditMode && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleRequestTrial}
              aria-label="Request trial access for this account"
            >
              <Headphones className="h-4 w-4 mr-2" aria-hidden="true" />
              Request Trial Access
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowMeetDialog(true)}
              aria-label="Schedule Google Meet"
            >
              <Video className="h-4 w-4 mr-2" aria-hidden="true" />
              Schedule Meeting
            </Button>
            <Button variant="outline" onClick={handleStartEdit} aria-label="Edit account">
              <Pencil className="h-4 w-4 mr-2" aria-hidden="true" />
              Edit
            </Button>
            {canDelete && (
              <Button
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
                aria-label={`Delete account ${account?.name || account?.account_name || ''}`}
              >
                <Trash2 className="h-4 w-4 mr-2" aria-hidden="true" />
                Delete
              </Button>
            )}
          </div>
        )}
      </div>

      <EditActionBar
        isEditMode={isEditMode}
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
        isSaving={updateMutation.isPending}
        saveLabel="Save Changes"
      />

      {/* Key Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Link
          to={`/contacts?accountId=${account?.id}`}
          className="block hover:opacity-80 transition-opacity"
          aria-label={`View all ${recentContacts.length} contacts for this account`}
        >
          <Card className="cursor-pointer hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">Contacts</CardTitle>
              <Users className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">{recentContacts.length}</div>
              <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                Click to view all →
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link
          to={`/opportunities?accountId=${account?.id}`}
          className="block hover:opacity-80 transition-opacity"
          aria-label={`View all ${recentOpportunities.length} opportunities for this account`}
        >
          <Card className="cursor-pointer hover:shadow-md transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
              <CardTitle className="text-sm font-medium">Opportunities</CardTitle>
              <DollarSign className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </CardHeader>
            <CardContent className="p-3">
              <div className="text-xl font-bold leading-none">{recentOpportunities.length}</div>
              <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                Click to view all →
              </p>
            </CardContent>
          </Card>
        </Link>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Locations</CardTitle>
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              {account?.number_of_locations__c || 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-tight">Office locations</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">
              <Badge
                variant={
                  formatCustomField(account?.status__c).startsWith('Active')
                    ? 'success'
                    : 'secondary'
                }
              >
                {formatCustomField(account?.status__c) || 'Unknown'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-tight">Account status</p>
          </CardContent>
        </Card>

        <Card>
          <AccountHealthCard health={accountHealth} isLoading={accountHealthLoading} />
        </Card>
      </div>

      {accountHealth && <NextBestAction type="account" accountHealth={accountHealth} />}

      {/* Main Content - Two Column Layout */}
      {/*
        Related Information holds thirteen tabs, and at md the 2/1 split leaves them dividing
        a third of the page, which is why their labels are squeezed to "Refs" and "Opps".
        From lg the split becomes 3/2, giving that panel 40% instead of 33%.

        md is deliberately left at 2/1: the page is already tight there, and taking a further
        7% from the detail fields would cost more than the tabs gain.
      */}
      <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-5">
        {/* Left Column - Account Details */}
        <div className="md:col-span-2 lg:col-span-3 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Core account details and contact information</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-0 sm:grid-cols-2">
                {renderField('Account Name', 'name', account?.name || account?.account_name, {
                  required: true,
                })}
                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Parent Account</dt>
                  <dd className="mt-1 text-sm">
                    {isEditMode ? (
                      <RecordCombobox
                        id="field-parent_account_id"
                        records={allAccounts
                          .filter((a) => a.id !== account?.id)
                          .slice()
                          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))}
                        value={(editForm.parent_account_id as string) || ''}
                        onValueChange={(v) => handleFieldChange('parent_account_id', v)}
                        displayField="name"
                        placeholder="Select parent account..."
                        searchPlaceholder="Search accounts..."
                        emptyMessage="No accounts found."
                        aria-label="Parent account"
                        selectedLabel={(account as any)?.parent_account_name}
                        totalCount={accountsData?.total ? accountsData.total - 1 : undefined}
                      />
                    ) : (account as any)?.parent_account_id &&
                      (account as any)?.parent_account_name ? (
                      <Link
                        to={`/accounts/${(account as any).parent_account_id}`}
                        className="text-primary hover:underline"
                      >
                        {(account as any).parent_account_name}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </dd>
                </div>
                {renderField('Type', 'type', account?.type, {
                  type: 'select',
                  selectOptions: ACCOUNT_TYPES,
                })}
                {renderField('Industry', 'industry', account?.industry)}
                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Account Currency</dt>
                  <dd className="mt-1 text-sm">
                    {isEditMode ? (
                      <CurrencySelector
                        id="account-currency"
                        hideOther
                        value={(editForm.currency_iso_code as string) || 'CAD'}
                        onValueChange={(v) => handleFieldChange('currency_iso_code', v)}
                      />
                    ) : account?.currency_iso_code ? (
                      `${account.currency_iso_code} - ${{ USD: 'U.S. Dollar', CAD: 'Canadian Dollar', EUR: 'Euro', GBP: 'British Pound', AUD: 'Australian Dollar', DKK: 'Danish Krone' }[account.currency_iso_code] || account.currency_iso_code}`
                    ) : (
                      'CAD - Canadian Dollar'
                    )}
                  </dd>
                </div>
                {renderField('Phone', 'phone', account?.phone, {
                  link: account?.phone ? { href: `tel:${account?.phone}` } : undefined,
                })}
                {renderField('Website', 'website', account?.website, {
                  link: account?.website
                    ? {
                        href: account?.website.startsWith('http')
                          ? account?.website
                          : `https://${account?.website}`,
                        external: true,
                      }
                    : undefined,
                })}
                {renderOwnerField()}
              </dl>
            </CardContent>
          </Card>

          {/* Billing Address - always show in edit mode, otherwise only if has address */}
          {(isEditMode || formatAddress()) && (
            <Card>
              <CardHeader>
                <CardTitle>Billing Address</CardTitle>
              </CardHeader>
              <CardContent>
                {isEditMode ? (
                  <dl className="grid grid-cols-1 gap-x-6 gap-y-0 sm:grid-cols-2">
                    <div className="sm:col-span-2 py-2 border-b border-border">
                      <AddressAutocomplete
                        id="billing_street"
                        label="Street"
                        required
                        value={(editForm.billing_street as string) || ''}
                        error={formErrors.billing_street}
                        onStreetChange={(street) => handleFieldChange('billing_street', street)}
                        onAddressSelect={(a: AddressDetails) =>
                          setEditForm((prev) => ({
                            ...prev,
                            billing_street: a.street || '',
                            billing_city: a.city || '',
                            billing_state: a.state || a.state_code || '',
                            billing_postal_code: a.postal_code || '',
                            billing_country: a.country || '',
                          }))
                        }
                        placeholder="Start typing address..."
                      />
                    </div>
                    {renderField('City', 'billing_city', account?.billing_city, { required: true })}
                    {renderField('State/Province', 'billing_state', account?.billing_state)}
                    {renderField(
                      'Postal Code',
                      'billing_postal_code',
                      account?.billing_postal_code
                    )}
                    {renderField('Country', 'billing_country', account?.billing_country)}
                  </dl>
                ) : (
                  <address className="not-italic text-sm">
                    {account?.billing_street && <div>{account?.billing_street}</div>}
                    <div>
                      {[account?.billing_city, account?.billing_state, account?.billing_postal_code]
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                    {account?.billing_country && <div>{account?.billing_country}</div>}
                  </address>
                )}
              </CardContent>
            </Card>
          )}

          {/* Company Details */}
          <Card>
            <CardHeader>
              <CardTitle>Company Details</CardTitle>
              <CardDescription>Financial and organizational information</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-0 sm:grid-cols-2">
                {renderField(
                  'Employees',
                  'number_of_employees',
                  account?.number_of_employees
                    ? formatNumber(account?.number_of_employees)
                    : undefined,
                  { type: 'number' }
                )}
                {renderField(
                  'Locations',
                  'number_of_locations__c',
                  account?.number_of_locations__c
                )}
                {renderField('Type of Shop', 'type_of_shop__c', account?.type_of_shop__c)}
                {isEditMode ? (
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">House Account</dt>
                    <dd className="mt-1">
                      <select
                        id="field-house_account__c"
                        value={editForm.house_account__c ? 'true' : 'false'}
                        onChange={(e) =>
                          handleFieldChange('house_account__c', e.target.value === 'true')
                        }
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </select>
                    </dd>
                  </div>
                ) : (
                  <div className="py-2 border-b border-border">
                    <dt className="text-sm font-medium text-muted-foreground">House Account</dt>
                    <dd className="mt-1 text-sm">
                      {account?.house_account__c ? (
                        <Badge variant="success">Yes</Badge>
                      ) : (
                        <Badge variant="secondary">No</Badge>
                      )}
                    </dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>

          {/* Custom Fields */}
          <Card>
            <CardHeader>
              <CardTitle>Custom Fields</CardTitle>
              <CardDescription>Additional account-specific information</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-0 sm:grid-cols-2">
                {renderField('Status', 'status__c', formatCustomField(account?.status__c))}
                {renderField('Marketing', 'marketing__c', account?.marketing__c)}
                {renderField('GDO Code', 'gdo_code__c', account?.gdo_code__c)}
                {renderField(
                  'Digital Accessibility Proficiency',
                  'digital_accessibility_proficiency__c',
                  account?.digital_accessibility_proficiency__c
                )}
                {renderField('Digital Policy', 'digital_policy__c', account?.digital_policy__c)}
                {renderField(
                  'Current Digital Accessibility Vendor',
                  'current_digital_accessibility_vendor__c',
                  account?.current_digital_accessibility_vendor__c
                )}
                {renderField('Campaign', 'campaign__c', account?.campaign__c)}
                {renderField('Billing Contact', 'billing_contact__c', account?.billing_contact__c)}
                {renderField(
                  'First Transaction Date',
                  'first_transaction_date__c',
                  account?.first_transaction_date__c,
                  { type: 'date' }
                )}
                {/* Read-only date fields */}
                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Last Won Date</dt>
                  <dd className="mt-1 text-sm">{formatDate(account?.last_won_date__c)}</dd>
                </div>
                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Next Task Date</dt>
                  <dd className="mt-1 text-sm">{formatDate(account?.next_task_date__c)}</dd>
                </div>
                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Next Event Date</dt>
                  <dd className="mt-1 text-sm">{formatDate(account?.next_event_date__c)}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {/* Description - always show in edit mode */}
          {(isEditMode || account?.description) && (
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                {isEditMode ? (
                  <textarea
                    id="field-description"
                    value={(editForm.description as string) || ''}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder="Enter account description..."
                    aria-label="Account description"
                  />
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{account?.description}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* System Information */}
          <Card>
            <CardHeader>
              <CardTitle>System Information</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 gap-x-6 gap-y-0 sm:grid-cols-2">
                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Created Date</dt>
                  <dd className="mt-1 text-sm">
                    {formatDate(account?.created_date)}
                    {account?.created_by_name && ` by ${account.created_by_name}`}
                  </dd>
                </div>
                <div className="py-2 border-b border-border">
                  <dt className="text-sm font-medium text-muted-foreground">Last Modified Date</dt>
                  <dd className="mt-1 text-sm">
                    {formatDate(account?.last_modified_date)}
                    {account?.last_modified_by_name && ` by ${account.last_modified_by_name}`}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Related Records */}
        <div className="md:col-span-1 lg:col-span-2">
          <Card className="h-[calc(100vh-7rem)] min-h-[800px] flex flex-col">
            <CardHeader>
              <CardTitle>Related Information</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden">
              <Tabs
                defaultValue={searchParams.get('tab') || 'activity'}
                className="h-full flex flex-col"
              >
                {/*
                  ONE list, not two.

                  This was two sibling <TabsList> elements stacked in a wrapper, splitting a
                  single 13-tab group 6/7 purely for width. That is a keyboard defect, not just
                  a cosmetic one: Radix gives every TabsList its own RovingFocusGroup and
                  Collection, so arrow keys dead-ended at "Emails" and could not reach row two.
                  A reader had to Tab out of one tablist and into another to cross a strip that
                  looks like one thing. One wrapping list registers all thirteen triggers in a
                  single roving-focus group, which is what the ARIA tabs pattern expects.

                  It also stops the rows needing hand-balancing every time a tab is added.

                  STYLE: underline rather than the shadcn default, which is a filled tray of
                  pills and reads as a button group. Colours are tokens (border-primary,
                  border-border), never hex, because the brand guardrail has a zero baseline.
                  Active state is a border appearing plus a text-colour shift, so it is not
                  carried by hue alone.
                */}
                <TabsList className="mb-2 flex h-auto w-full flex-wrap justify-start gap-1 rounded-none border-b border-border bg-transparent p-0">
                  <TabsTrigger value="contacts" className={TAB_CLASS}>
                    Contacts
                  </TabsTrigger>
                  <TabsTrigger value="references" className={TAB_CLASS}>
                    Refs
                  </TabsTrigger>
                  <TabsTrigger value="opportunities" className={TAB_CLASS}>
                    Opps
                  </TabsTrigger>
                  <TabsTrigger value="leads" className={TAB_CLASS}>
                    Leads{matchedLeads.length > 0 ? ` (${matchedLeads.length})` : ''}
                  </TabsTrigger>
                  <TabsTrigger value="rfps" className={TAB_CLASS}>
                    RFPs
                  </TabsTrigger>
                  <TabsTrigger value="emails" className={TAB_CLASS}>
                    Emails
                  </TabsTrigger>
                  <TabsTrigger value="tickets" className={TAB_CLASS}>
                    Tickets
                  </TabsTrigger>
                  <TabsTrigger value="children" className={TAB_CLASS}>
                    Children
                  </TabsTrigger>
                  <TabsTrigger value="notes" className={TAB_CLASS}>
                    Notes ({notes.length + accountNotes.length + (noteSessions?.length ?? 0)})
                  </TabsTrigger>
                  <TabsTrigger value="attachments" className={TAB_CLASS}>
                    Files
                  </TabsTrigger>
                  <TabsTrigger value="contracts" className={TAB_CLASS}>
                    Contracts
                  </TabsTrigger>
                  <TabsTrigger value="activity" className={TAB_CLASS}>
                    Activity
                  </TabsTrigger>
                  <TabsTrigger value="history" className={TAB_CLASS}>
                    History
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="contacts" className="flex-1 overflow-y-auto mt-4 space-y-4">
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCreateContactModalOpen(true)}
                      aria-label="Create new contact for this account"
                    >
                      <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
                      New Contact
                    </Button>
                  </div>
                  {recentContacts.length > 0 ? (
                    recentContacts.map((contact: any) => (
                      <Card key={contact.id} className="p-3">
                        <div className="space-y-2">
                          <div className="font-medium flex items-center gap-2">
                            <Link
                              to={`/contacts/${contact.id}`}
                              className="text-primary hover:underline"
                            >
                              {contact.first_name} {contact.last_name}
                            </Link>
                            {contact.is_reference && (
                              <Badge
                                variant="default"
                                className="text-xs px-1.5 py-0"
                                style={{
                                  backgroundColor: GRACKLE_COLORS.pollen,
                                  color: GRACKLE_COLORS.prussianBlue,
                                }}
                              >
                                <Star className="h-2.5 w-2.5 mr-0.5" aria-hidden="true" />
                                Ref
                              </Badge>
                            )}
                          </div>
                          {contact.title && (
                            <div className="text-sm text-muted-foreground">{contact.title}</div>
                          )}
                          {contact.email && (
                            <div className="text-sm">
                              <a
                                href={`mailto:${contact.email}`}
                                className="text-primary hover:underline"
                              >
                                {contact.email}
                              </a>
                            </div>
                          )}
                          {contact.phone && (
                            <div className="text-sm">
                              <ClickToCallButton
                                phoneNumber={contact.phone}
                                whoId={contact.id}
                                whoType="Contact"
                                whoName={`${contact.first_name} ${contact.last_name}`}
                                whatId={id}
                                whatType="Account"
                              />
                            </div>
                          )}
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center text-sm text-muted-foreground py-8">
                      No contacts found for this account
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="references" className="flex-1 overflow-y-auto mt-4 space-y-4">
                  {recentContacts.filter((c: any) => c.is_reference).length > 0 ? (
                    recentContacts
                      .filter((c: any) => c.is_reference)
                      .map((contact: any) => (
                        <Card key={contact.id} className="p-3">
                          <div className="space-y-2">
                            <div className="font-medium flex items-center gap-2">
                              <Star
                                className="h-4 w-4"
                                style={{ color: CATEGORICAL.clay.solid }}
                                aria-hidden="true"
                              />
                              <Link
                                to={`/contacts/${contact.id}`}
                                className="text-primary hover:underline"
                              >
                                {contact.first_name} {contact.last_name}
                              </Link>
                            </div>
                            {contact.title && (
                              <div className="text-sm text-muted-foreground">{contact.title}</div>
                            )}
                            {contact.reference_consent_date && (
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Consent Date:</span>
                                <span>{formatDate(contact.reference_consent_date)}</span>
                              </div>
                            )}
                            {contact.reference_consent_notes && (
                              <div className="text-sm">
                                <span className="text-muted-foreground">Notes: </span>
                                <span className="whitespace-pre-wrap">
                                  {contact.reference_consent_notes}
                                </span>
                              </div>
                            )}
                            {contact.email && (
                              <div className="text-sm">
                                <a
                                  href={`mailto:${contact.email}`}
                                  className="text-primary hover:underline"
                                >
                                  {contact.email}
                                </a>
                              </div>
                            )}
                            {contact.phone && (
                              <div className="text-sm">
                                <ClickToCallButton
                                  phoneNumber={contact.phone}
                                  whoId={contact.id}
                                  whoType="Contact"
                                  whoName={`${contact.first_name} ${contact.last_name}`}
                                  whatId={id}
                                  whatType="Account"
                                />
                              </div>
                            )}
                          </div>
                        </Card>
                      ))
                  ) : (
                    <div className="text-center text-sm text-muted-foreground py-8">
                      No reference contacts for this account
                    </div>
                  )}
                </TabsContent>

                <TabsContent
                  value="opportunities"
                  className="flex-1 overflow-y-auto mt-4 space-y-4"
                >
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCreateOpportunityModalOpen(true)}
                      aria-label="Create new opportunity for this account"
                    >
                      <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
                      New Opportunity
                    </Button>
                  </div>
                  {recentOpportunities.length > 0 ? (
                    recentOpportunities.map((opportunity: any) => (
                      <Card key={opportunity.id} className="p-3">
                        <div className="space-y-2">
                          <div className="font-medium">
                            <Link
                              to={`/opportunities/${opportunity.id}`}
                              className="text-primary hover:underline"
                            >
                              {opportunity.opportunity_name}
                            </Link>
                          </div>
                          {opportunity.stage_name && (
                            <div>
                              <Badge variant="default">{opportunity.stage_name}</Badge>
                            </div>
                          )}
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Amount:</span>
                            <span className="font-medium">
                              {formatCurrency(
                                opportunity.amount,
                                opportunity.currency_iso_code || 'CAD'
                              )}
                            </span>
                          </div>
                          {opportunity.close_date && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Close Date:</span>
                              <span>{formatDate(opportunity.close_date)}</span>
                            </div>
                          )}
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center text-sm text-muted-foreground py-8">
                      No opportunities found for this account
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="rfps" className="flex-1 overflow-y-auto mt-4 space-y-4">
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCreateRfpModalOpen(true)}
                      aria-label="Create new RFP for this account"
                    >
                      <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
                      New RFP
                    </Button>
                  </div>
                  {rfps.length > 0 ? (
                    rfps.map((rfp: any) => (
                      <Card key={rfp.id} className="p-3">
                        <div className="space-y-2">
                          <div className="font-medium flex items-center gap-2">
                            <FileText
                              className="h-4 w-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                            <Link to={`/rfps/${rfp.id}`} className="text-primary hover:underline">
                              {rfp.name}
                            </Link>
                            <Badge
                              variant={
                                rfp.stage === 'Won'
                                  ? 'default'
                                  : rfp.stage === 'Lost'
                                    ? 'destructive'
                                    : 'secondary'
                              }
                              className="text-xs"
                            >
                              {rfp.stage}
                            </Badge>
                          </div>
                          {rfp.estimated_value && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Estimated Value:</span>
                              <span className="font-medium">
                                {formatCurrency(rfp.estimated_value)}
                              </span>
                            </div>
                          )}
                          {rfp.submission_deadline && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Submission Deadline:</span>
                              <span>{formatDate(rfp.submission_deadline)}</span>
                            </div>
                          )}
                          {rfp.bid_number && (
                            <div className="text-xs text-muted-foreground">
                              Bid: {rfp.bid_number}
                            </div>
                          )}
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center text-sm text-muted-foreground py-8">
                      No RFPs found for this account
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="emails" className="flex-1 overflow-y-auto mt-4">
                  <RelatedEmails entityType="account" entityId={id!} maxHeight="600px" />
                </TabsContent>

                <TabsContent value="tickets" className="flex-1 overflow-y-auto mt-4 space-y-4">
                  <div className="flex justify-end">
                    {canManageTickets && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCreateTicketModalOpen(true)}
                        aria-label="Create new ticket for this account"
                      >
                        <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
                        New Ticket
                      </Button>
                    )}
                  </div>
                  {tickets.length > 0 ? (
                    tickets.map((ticket: any) => (
                      <Card key={ticket.id} className="p-3">
                        <div className="space-y-2">
                          <div className="font-medium flex items-center gap-2">
                            <Headphones
                              className="h-4 w-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                            <Link
                              to={`/tickets/${ticket.id}`}
                              className="text-primary hover:underline"
                            >
                              GRK-{ticket.ticket_number}: {ticket.subject}
                            </Link>
                            <Badge
                              variant={(ticketStatusVariants as any)[ticket.status] || 'secondary'}
                              className="text-xs"
                            >
                              {ticket.status}
                            </Badge>
                            <Badge
                              variant={
                                (ticketPriorityVariants as any)[ticket.priority] || 'outline'
                              }
                              className="text-xs"
                            >
                              {ticket.priority}
                            </Badge>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Owner: {ticket.owner_name || 'Unassigned'}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              {ticket.created_date
                                ? (
                                    toDisplayDate(ticket.created_date) ??
                                    new Date(ticket.created_date)
                                  ).toLocaleDateString()
                                : ''}
                            </span>
                          </div>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center text-sm text-muted-foreground py-8">
                      No tickets found for this account
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="children" className="flex-1 overflow-y-auto mt-4 space-y-2">
                  {(account as any)?.child_accounts &&
                  (account as any).child_accounts.length > 0 ? (
                    (account as any).child_accounts.map((child: any) => (
                      <Card key={child.id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <Link
                              to={`/accounts/${child.id}`}
                              className="text-sm font-medium text-primary hover:underline"
                            >
                              {child.name}
                            </Link>
                            <div className="flex items-center gap-2 mt-0.5">
                              {child.type && (
                                <Badge variant="outline" className="text-xs">
                                  {child.type}
                                </Badge>
                              )}
                              {child.industry && (
                                <span className="text-xs text-muted-foreground">
                                  {child.industry}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center text-sm text-muted-foreground py-8">
                      No child accounts
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="leads" className="flex-1 overflow-y-auto mt-4 space-y-2">
                  {matchedLeads.length > 0 ? (
                    matchedLeads.map((lead: any) => (
                      <Card key={lead.id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <Link
                              to={`/leads/${lead.id}`}
                              className="text-sm font-medium text-primary hover:underline"
                            >
                              {lead.name ||
                                `${lead.first_name || ''} ${lead.last_name || ''}`.trim()}
                            </Link>
                            <div className="flex items-center gap-2 mt-0.5">
                              {lead.status && (
                                <Badge variant="outline" className="text-xs">
                                  {lead.status}
                                </Badge>
                              )}
                              {lead.email && (
                                <span className="text-xs text-muted-foreground">{lead.email}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center text-sm text-muted-foreground py-8">
                      No leads matched to this account by email domain
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="notes" className="flex-1 overflow-y-auto mt-4 space-y-4">
                  {/* Editable notes (call sessions + quick notes), mirrors OpportunityDetail */}
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold">Notes</h3>
                    <Button
                      size="sm"
                      onClick={() => {
                        setExistingSessionId(undefined)
                        setNotePanelOpen(true)
                      }}
                      disabled={!!impersonatedUser}
                      aria-label="Add note"
                    >
                      <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                      Add Note
                    </Button>
                  </div>
                  <MergedNotesList
                    entityType="Account"
                    entityId={id!}
                    legacyNotes={accountNotes}
                    currentUserId={currentUser?.id}
                    currentUserType={currentUser?.user_type}
                    onOpenSession={(sessionId) => {
                      setExistingSessionId(sessionId)
                      setNotePanelOpen(true)
                    }}
                    onOpenLegacyNote={(note) => {
                      setSelectedNoteId(note.id)
                      setNoteDetailModalOpen(true)
                    }}
                    onEditNote={(note) => {
                      setEditingNote(note)
                      setEditNoteModalOpen(true)
                    }}
                    onDeleteNote={(note) => {
                      deleteNoteMutation.mutate(note.id)
                    }}
                    onEditSession={(session) => {
                      setEditingSession(session)
                      setExistingSessionId(session.id)
                      setNotePanelOpen(true)
                    }}
                    onDeleteSession={(session) => {
                      deleteSessionMutation.mutate(session.id)
                    }}
                  />

                  {/* Imported notes (read-only SNOTE content) */}
                  {notes.length > 0 && (
                    <div className="pt-4 border-t">
                      <h3 className="text-sm font-semibold mb-2">Imported Notes</h3>
                      <div className="divide-y">
                        {notes.map((note: any) => (
                          <div key={note.id} className="py-3 first:pt-0">
                            <button
                              className="text-sm font-medium text-primary hover:underline text-left cursor-pointer"
                              onClick={() => {
                                setSelectedContentId(note.id)
                                setContentDetailModalOpen(true)
                              }}
                              aria-label={`View note: ${note.title || 'Note'}`}
                            >
                              {note.title || 'Note'}
                            </button>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {note.created_date
                                ? (
                                    toDisplayDate(note.created_date) ?? new Date(note.created_date)
                                  ).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                  })
                                : ''}
                              {note.created_date
                                ? ', ' +
                                  new Date(note.created_date)
                                    .toLocaleTimeString('en-US', {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true,
                                    })
                                    .toLowerCase()
                                : ''}
                              {note.owner_name ? ` by ${note.owner_name}` : ''}
                            </p>
                            {note.text_preview && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {note.text_preview}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="attachments" className="flex-1 overflow-y-auto mt-4">
                  <AttachmentsTab entityId={id!} entityType="Account" />
                </TabsContent>

                {/*
                  A link into this customer's Drive folder. The CRM stores where the
                  contracts are, never what is in them: the folder opens under the user's
                  own Google session, so Drive's sharing still decides who can read them.

                  Editing the link reuses `canDelete`'s condition rather than bare
                  `isAdmin`, because it must also be blocked while impersonating a
                  non-admin. The app-wide read-only guard does NOT fire (blockMutations
                  runs before authenticate sets req.isReadOnly), so gating here is the
                  actual control, not a cosmetic one.
                */}
                <TabsContent value="contracts" className="flex-1 overflow-y-auto mt-4">
                  <ContractsTab
                    accountId={id!}
                    accountName={account?.name || account?.account_name}
                    contractsFolderUrl={account?.contracts_folder_url}
                    canEdit={canDelete}
                  />
                </TabsContent>

                <TabsContent value="activity" className="flex-1 overflow-y-auto mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold">Activity</h3>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCreateTaskModalOpen(true)}
                      aria-label="Create new task for this account"
                    >
                      <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
                      New Task
                    </Button>
                  </div>
                  <ActivityTimeline entityId={id!} entityType="Account" />
                </TabsContent>

                <TabsContent value="history" className="flex-1 overflow-y-auto mt-4">
                  <HistoryTimeline entityId={id!} entityType="Account" />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Content Detail Modal (for viewing SNOTE notes) */}
      <ContentDetailModal
        contentId={selectedContentId}
        open={contentDetailModalOpen}
        onOpenChange={setContentDetailModalOpen}
      />

      {/* Delete Account confirmation (Admin/Super Admin only) */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent aria-describedby="delete-account-description">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account</AlertDialogTitle>
            <AlertDialogDescription id="delete-account-description">
              Are you sure you want to delete the account "{account?.name || account?.account_name}
              "? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel delete operation">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteAccountMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label={`Confirm delete account ${account?.name || account?.account_name || ''}`}
            >
              {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Legacy note detail (read-only view of core_crm.notes) */}
      <NoteDetailModal
        noteId={selectedNoteId}
        open={noteDetailModalOpen}
        onOpenChange={setNoteDetailModalOpen}
        currentUserId={currentUser?.id}
        currentUserType={currentUser?.user_type}
        onEdit={(note) => {
          setSelectedNoteId(null)
          setNoteDetailModalOpen(false)
          setEditingNote(note)
          setEditNoteModalOpen(true)
        }}
        onDelete={(note) => {
          deleteNoteMutation.mutate(note.id)
          setNoteDetailModalOpen(false)
        }}
      />

      {/* Edit Note Modal */}
      <EditNoteModal
        open={editNoteModalOpen}
        onOpenChange={setEditNoteModalOpen}
        note={editingNote}
        parentId={id!}
      />

      {/* Note Session Panel (create/edit editable notes) */}
      {account && (
        <NoteSessionPanel
          entityType="Account"
          entityId={id!}
          entityName={account.name || (account as any).account_name}
          open={notePanelOpen}
          onOpenChange={(open) => {
            setNotePanelOpen(open)
            if (!open) {
              setExistingSessionId(undefined)
              setEditingSession(null)
            }
          }}
          readOnly={!!impersonatedUser}
          existingSessionId={existingSessionId}
          allowEdit={editingSession?.id === existingSessionId}
        />
      )}

      {/* Create Contact Modal */}
      <CreateContactModal
        open={createContactModalOpen}
        onOpenChange={setCreateContactModalOpen}
        accountId={account?.id}
        ownerId={account?.owner_id}
      />

      {/* Create Opportunity Modal */}
      <CreateOpportunityModal
        open={createOpportunityModalOpen}
        onOpenChange={setCreateOpportunityModalOpen}
        accountId={account?.id}
        accountName={account?.name || ''}
        ownerId={account?.owner_id}
      />

      {/* Create Ticket Modal */}
      <CreateTicketModal
        open={createTicketModalOpen}
        onOpenChange={setCreateTicketModalOpen}
        accountId={account?.id}
      />

      {/* Create RFP Modal */}
      <CreateRfpModal
        open={createRfpModalOpen}
        onOpenChange={setCreateRfpModalOpen}
        accountId={account?.id}
      />

      {/* Create Task Modal */}
      <CreateTaskModal
        open={createTaskModalOpen}
        onOpenChange={setCreateTaskModalOpen}
        relatedToId={account?.id}
        relatedToType="Account"
        relatedToName={account?.name || account?.account_name}
        ownerId={account?.owner_id}
      />

      {/* Schedule Meet Dialog */}
      {account && (
        <ScheduleMeetDialog
          open={showMeetDialog}
          onOpenChange={setShowMeetDialog}
          entityType="account"
          entityId={account.id}
          defaultTitle={`Meeting about ${account.name || account.account_name}`}
          defaultAttendees={recentContacts
            .filter((c: any) => c.email)
            .slice(0, 5)
            .map((c: any) => c.email)}
        />
      )}
    </div>
  )
}
